import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const BrutalistLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;
  const cust = quote.customerData || {};
  const ink = theme.textPrimary;
  const yellow = theme.accent || '#ffe14d';
  const box = { border: `2px solid ${ink}`, background: theme.surface };
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 0, background: '#fff', border: `2px solid ${ink}`, color: ink, cursor: 'pointer', fontSize: 12, textTransform: 'uppercase' };

  const Row = ({ service, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '12px 0', borderBottom: `2px dashed ${ink}`, opacity: isOpt && !on ? 0.45 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isOpt
                ? <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 16, height: 16, accentColor: ink, cursor: 'pointer' }} />
                : <span style={{ fontWeight: 700 }}>[x]</span>}
              <p style={{ fontSize: 14, fontWeight: 700, color: ink, margin: 0, textTransform: 'uppercase' }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4, lineHeight: 1.5 }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: ink, whiteSpace: 'nowrap' }}>{formatEUR(lineTotal(service))}{suffix ? ` ${suffix}` : ''}</span>
        </div>
      </div>
    );
  };
  const Group = ({ title, services, suffix }) => services.length ? (
    <div style={{ ...box, padding: '12px 14px', marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', background: ink, color: theme.surface, display: 'inline-block', padding: '2px 8px', marginBottom: 6 }}>{title}</div>
      {services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} />)}
    </div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: ink, padding: '0 0 56px' }}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '18px 22px' }}>
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 14 }}>
          <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
          <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'OK' : 'Link'}</button>
        </div>

        {/* Header block */}
        <div style={{ ...box, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>// angebot_{String(quote.quoteNumber || '').replace(/\W/g, '_').toLowerCase()}</div>
          <h1 style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.02, margin: '8px 0 0', textTransform: 'uppercase' }}>{cust.company || quote.heroHeadline || 'ANGEBOT'}</h1>
          {cust.name && <div style={{ fontSize: 12, marginTop: 6 }}>→ z.Hd. {cust.name}</div>}
        </div>
        <div style={{ ...box, borderTop: 'none', padding: '12px 16px', background: yellow, fontSize: 15, fontWeight: 700 }}>
          GESAMT: {formatEUR(einmaligTotals.brutto)}
          {monatlichTotals.brutto > 0 ? ` + ${formatEUR(monatlichTotals.brutto)}/MO` : ''} →
        </div>

        {quote.quoteIntro && <div style={{ ...box, borderTop: 'none', padding: '12px 16px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</div>}
        {quote.customerArguments && (
          <div style={{ ...box, borderTop: 'none', padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{'>'} WARUM WIR:</div>
            <div dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}
        {quote.videoUrl && <div className="no-print" style={{ ...box, borderTop: 'none', padding: 8 }}><video src={quote.videoUrl} controls style={{ width: '100%', display: 'block' }} /></div>}

        <Group title="Einmalig" services={einmaligServices} suffix="" />
        <Group title="Monatlich" services={monatlichServices} suffix="/MO" />
        <Group title="Jaehrlich" services={jaehrlichServices} suffix="/JAHR" />

        <button onClick={() => setShowAcceptanceForm(true)} className="no-print" style={{ width: '100%', padding: '16px', background: yellow, color: ink, border: `2px solid ${ink}`, borderTop: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
          [ ANGEBOT ANNEHMEN ]
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <TermsBlock quote={quote} company={company} theme={theme} />
          <AcceptanceBlock ctx={ctx} theme={theme} />
          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FeedbackBlock viewHash={viewHash} theme={theme} />
            <QuestionBlock viewHash={viewHash} theme={theme} />
          </div>
          <FooterBlock company={company} theme={theme} />
        </div>
      </div>
    </div>
  );
};

export default BrutalistLayout;
